import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import PageHeader from '../../components/PageHeader';
import { themeBackgrounds } from '../../theme/theme';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { encryptResponse } from '../../services';

interface CreateChatBotProps {
  onCancel: () => void;
  onCreated: () => void;
}

interface ChatBotDetails {
  bot_name: string;
  welcome_message: string;
  no_result_message: string;
  handover_message: string;
}

interface CreateChatBotResponse {
  status: number;
  message?: string;
  data?: any;
}

const DEFAULT_WELCOME_MESSAGE = 'Hi! How can I help you today?';
const DEFAULT_NO_RESULT_MESSAGE = "Sorry, I couldn't find an answer to that. Please try asking in a different way.";
const DEFAULT_HANDOVER_MESSAGE = "I'm connecting you with a support agent who can help you further.";

const CreateChatBot: React.FC<CreateChatBotProps> = ({ onCancel, onCreated }) => {
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const applicationDetails = useSelector((state: any) => state.applicationDetails);
  const [isLoading, setIsLoading] = useState(false);
  const [details, setDetails] = useState<ChatBotDetails>({
    bot_name: '',
    welcome_message: DEFAULT_WELCOME_MESSAGE,
    no_result_message: DEFAULT_NO_RESULT_MESSAGE,
    handover_message: DEFAULT_HANDOVER_MESSAGE,
  });

  const handleChange = (field: keyof ChatBotDetails) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setDetails((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const isValid = Boolean(details.bot_name.trim());

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid) {
      toast.error('Please enter a chat bot name.', { position: 'top-right' });
      return;
    }

    const organizationId = userDetails?.organization_id || '';
    const applicationId = applicationDetails?.app_id || '';

    if (!organizationId || !applicationId) {
      toast.error('Organization or application details are missing. Please try again.', {
        position: 'top-right',
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        data: await encryptResponse({
          organization_id: organizationId,
          application_id: applicationId,
          bot_name: details.bot_name.trim(),
          welcome_message: details.welcome_message.trim(),
          no_result_message: details.no_result_message.trim(),
          handover_message: details.handover_message.trim(),
        }, REQ_ENC_KEY),
      };

      const response = await httpService.post<CreateChatBotResponse>(
        `${API_BASE_URL}/user/createChatBot`,
        payload
      );

      if (response.data?.status === 200) {
        toast.success(response.data.message || 'Chat bot created successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });
        onCreated();
      } else {
        toast.error(response.data?.message || 'Failed to create chat bot. Please try again.', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        'An error occurred while creating the chat bot. Please try again.';
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', mx: 'auto' }}>
      <PageHeader
        heading="Create Chat Bot"
        subheading="Enter the details for your chat bot. These settings define how it introduces itself and replies to users."
        variant="gradient"
      />

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Chat Bot Details
            </Typography>

            <TextField
              label="Chat bot name"
              placeholder="Enter chat bot name"
              fullWidth
              required
              value={details.bot_name}
              onChange={handleChange('bot_name')}
              disabled={isLoading}
              sx={{ mb: 3 }}
            />

            <TextField
              label="Welcome message"
              placeholder={DEFAULT_WELCOME_MESSAGE}
              helperText="Default message is shown. You can edit it as needed."
              fullWidth
              multiline
              minRows={2}
              value={details.welcome_message}
              onChange={handleChange('welcome_message')}
              disabled={isLoading}
              sx={{ mb: 3 }}
            />

            <TextField
              label="No result message"
              placeholder={DEFAULT_NO_RESULT_MESSAGE}
              helperText="Shown when the bot cannot find an answer. You can edit this default."
              fullWidth
              multiline
              minRows={2}
              value={details.no_result_message}
              onChange={handleChange('no_result_message')}
              disabled={isLoading}
              sx={{ mb: 3 }}
            />

            <TextField
              label="Handover message"
              placeholder={DEFAULT_HANDOVER_MESSAGE}
              helperText="Shown when handing over to a live agent. You can edit this default."
              fullWidth
              multiline
              minRows={2}
              value={details.handover_message}
              onChange={handleChange('handover_message')}
              disabled={isLoading}
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!isValid || isLoading}
                startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
                sx={{
                  background: (!isValid || isLoading) ? 'grey' : themeBackgrounds.default,
                  color: '#ffffff',
                  '&:hover': {
                    background: themeBackgrounds.default,
                    opacity: 0.9,
                  },
                }}
              >
                {isLoading ? 'Creating...' : 'Create Chat Bot'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateChatBot;
